import { formateurFlowsListPath } from '@/lib/platform';

export const EDUFLOW_RETURN_TO_QUERY = 'returnTo';
const SESSION_KEY = 'lyrra_eduflow_designer_return';

const INSTITUTIONISH_ROLES = new Set([
  'institution',
  'institution_admin',
  'drh',
  'rh',
  'resp_formation',
]);

function isSafeInternalPath(p: string): boolean {
  const s = p.trim();
  return s.startsWith('/') && !s.startsWith('//') && !s.includes('://');
}

/** Cible par défaut si aucun `returnTo` / state / stockage session. */
export function defaultEduFlowExitPathForRole(userRole: string | null | undefined): string {
  const r = (userRole ?? '').toLowerCase().trim();
  if (INSTITUTIONISH_ROLES.has(r)) return '/institution?tab=flows';
  return formateurFlowsListPath();
}

/**
 * Cible du bouton retour du designer (et sorties d’erreur) : priorité au query `returnTo`,
 * puis `location.state.from`, puis sessionStorage, puis rôle.
 */
export function resolveEduFlowDesignerExitPath(params: {
  returnToFromSearch: string | null;
  stateFrom: unknown;
  userRole: string | null | undefined;
}): string {
  const { returnToFromSearch, stateFrom, userRole } = params;

  if (returnToFromSearch) {
    try {
      const decoded = decodeURIComponent(returnToFromSearch.trim());
      if (isSafeInternalPath(decoded)) return decoded;
    } catch {
      /* ignore */
    }
  }

  if (typeof stateFrom === 'string' && isSafeInternalPath(stateFrom)) {
    return stateFrom;
  }

  try {
    const stored = sessionStorage.getItem(SESSION_KEY);
    if (stored && isSafeInternalPath(stored)) {
      sessionStorage.removeItem(SESSION_KEY);
      return stored;
    }
  } catch {
    /* ignore */
  }

  return defaultEduFlowExitPathForRole(userRole);
}

/** À appeler avant d’ouvrir le designer depuis un écran qui n’utilise pas `?returnTo=`. */
export function rememberEduFlowDesignerReturn(path: string): void {
  if (!isSafeInternalPath(path)) return;
  try {
    sessionStorage.setItem(SESSION_KEY, path);
  } catch {
    /* ignore */
  }
}

/** Lien vers le designer avec retour explicite (query `returnTo`). */
export function buildEduFlowEditUrl(flowId: string, returnTo?: string): string {
  const base = `/eduflow/edit/${flowId}`;
  if (!returnTo || !isSafeInternalPath(returnTo)) return base;
  return `${base}?${EDUFLOW_RETURN_TO_QUERY}=${encodeURIComponent(returnTo)}`;
}
